import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Spinner } from '@ng-unifi/ui';
import { apiClient, ApiError } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { policyStateLabel } from '../lib/humanLabels.js';

interface CredentialPolicy {
  touch_credential_enabled: boolean;
  third_party_nfc_enabled: boolean;
  pin_code_enabled: boolean;
  touch_pass_management_url: string | null;
  enrollment_reader_mode: 'any' | 'specified';
  enrollment_reader_id: string | null;
  enrollment_reader_name: string | null;
}

interface CallRoutingAutomationStatus {
  enabled: boolean;
  ready: boolean;
  mode: 'disabled' | 'missing_config' | 'profile_missing' | 'executor_pending' | 'ready';
  requirements: string[];
  config: {
    profile_dir: string | null;
    console_url_configured: boolean;
    profile_dir_exists: boolean;
    username: string | null;
    password_configured: boolean;
    totp_secret_configured: boolean;
  };
}

interface CallRoutingAutomationForm {
  enabled: boolean;
  username: string;
  password: string;
  totp_secret: string;
  profile_dir: string;
  console_url: string;
}

interface BuildingResource {
  id: string;
  unifi_resource_type: 'door' | 'door_group' | 'device';
  unifi_resource_id: string;
  name: string;
  full_name: string | null;
  metadata_json?: unknown;
}

interface EnrollmentReaderOption {
  id: string;
  name: string;
  kind: string;
  status: string;
}

interface HardwareInventoryResponse {
  data: {
    devices: Array<{
      id: string;
      unifi_device_id?: string;
      name: string;
      kind: string;
      status?: string;
    }>;
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function resourceToEnrollmentReader(resource: BuildingResource): EnrollmentReaderOption | null {
  if (resource.unifi_resource_type !== 'device') return null;
  const metadata = asRecord(resource.metadata_json);
  const kind = toStringOrNull(metadata.kind) ?? 'unknown';
  if (!isEnrollmentReaderCandidate(kind, resource.name)) return null;
  return {
    id: resource.unifi_resource_id,
    name: resource.full_name ?? resource.name ?? resource.unifi_resource_id,
    kind,
    status: toStringOrNull(metadata.status) ?? 'unknown',
  };
}

function inventoryDeviceToEnrollmentReader(
  device: HardwareInventoryResponse['data']['devices'][number],
): EnrollmentReaderOption | null {
  if (!isEnrollmentReaderCandidate(device.kind, device.name)) return null;
  return {
    id: device.unifi_device_id ?? device.id,
    name: device.name,
    kind: device.kind,
    status: device.status ?? 'unknown',
  };
}

function isEnrollmentReaderCandidate(kind: string, name: string): boolean {
  if (kind === 'reader' || kind === 'intercom') return true;
  if (kind === 'hub') return false;
  return /reader|intercom|entry|callbox|doorbell/i.test(name);
}

function mergeReaderOptions(...groups: EnrollmentReaderOption[][]): EnrollmentReaderOption[] {
  const seen = new Set<string>();
  const rows: EnrollmentReaderOption[] = [];
  for (const reader of groups.flat()) {
    if (seen.has(reader.id)) continue;
    seen.add(reader.id);
    rows.push(reader);
  }
  return rows;
}

function readerOptionLabel(reader: EnrollmentReaderOption): string {
  const status = reader.status === 'online' ? '' : ` (${reader.status})`;
  return `${reader.name} - ${reader.kind}${status}`;
}

export function AdminCredentialsPage() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<CredentialPolicy | null>(null);
  const [nextPolicy, setNextPolicy] = useState<CredentialPolicy | null>(null);
  const [reason, setReason] = useState('');
  const [enableAck, setEnableAck] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [readers, setReaders] = useState<EnrollmentReaderOption[]>([]);
  const [readerLoadError, setReaderLoadError] = useState<string | null>(null);
  const [callRouting, setCallRouting] = useState<CallRoutingAutomationStatus | null>(null);
  const [callRoutingForm, setCallRoutingForm] = useState<CallRoutingAutomationForm>({
    enabled: false,
    username: '',
    password: '',
    totp_secret: '',
    profile_dir: '/var/lib/ng-unifi/unifi-access-profile',
    console_url: '',
  });
  const [savingCallRouting, setSavingCallRouting] = useState(false);

  const isMaster = state.status === 'authed' && state.user.role === 'master_admin';
  const enablingTouch =
    !!policy &&
    !!nextPolicy &&
    !policy.touch_credential_enabled &&
    nextPolicy.touch_credential_enabled;
  const enablingNfc =
    !!policy &&
    !!nextPolicy &&
    !policy.third_party_nfc_enabled &&
    nextPolicy.third_party_nfc_enabled;
  const enablingRiskyFlow = enablingTouch || enablingNfc;

  useEffect(() => {
    if (state.status !== 'authed') return;
    if (!isMaster) {
      navigate('/', { replace: true });
    }
  }, [state, isMaster, navigate]);

  useEffect(() => {
    if (!isMaster) return;
    let cancelled = false;
    setError(null);
    setReaderLoadError(null);
    setPolicy(null);
    setNextPolicy(null);
    apiClient
      .get<{ data: CredentialPolicy }>('/api/v1/admin/credentials/policy', {
        skipTenantScope: true,
      })
      .then((res) => {
        if (cancelled) return;
        setPolicy(res.data);
        setNextPolicy(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load credential policy.');
      });
    Promise.all([
      apiClient
        .get<{ data: BuildingResource[] }>('/api/v1/admin/hardware/resources', {
          skipTenantScope: true,
        })
        .then((res) =>
          res.data
            .map(resourceToEnrollmentReader)
            .filter((reader): reader is EnrollmentReaderOption => reader !== null),
        )
        .catch(() => [] as EnrollmentReaderOption[]),
      apiClient
        .get<HardwareInventoryResponse>('/api/v1/admin/hardware/inventory', {
          skipTenantScope: true,
        })
        .then((res) =>
          res.data.devices
            .map(inventoryDeviceToEnrollmentReader)
            .filter((reader): reader is EnrollmentReaderOption => reader !== null),
        )
        .catch(() => [] as EnrollmentReaderOption[]),
    ])
      .then(([resourceReaders, liveReaders]) => {
        if (cancelled) return;
        setReaders(mergeReaderOptions(liveReaders, resourceReaders));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReaders([]);
        setReaderLoadError(
          err instanceof ApiError ? err.message : 'Failed to load discovered enrollment readers.',
        );
      });
    apiClient
      .get<{ data: CallRoutingAutomationStatus }>(
        '/api/v1/admin/callboxes/call-routing-automation',
        {
          skipTenantScope: true,
        },
      )
      .then((res) => {
        if (cancelled) return;
        setCallRouting(res.data);
        setCallRoutingForm({
          enabled: res.data.enabled,
          username: res.data.config.username ?? '',
          password: '',
          totp_secret: '',
          profile_dir: res.data.config.profile_dir ?? '/var/lib/ng-unifi/unifi-access-profile',
          console_url: '',
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Failed to load call routing settings.');
      });
    return () => {
      cancelled = true;
    };
  }, [isMaster]);

  const readerOptions = useMemo(() => {
    if (
      !nextPolicy ||
      nextPolicy.enrollment_reader_mode !== 'specified' ||
      !nextPolicy.enrollment_reader_id ||
      readers.some((reader) => reader.id === nextPolicy.enrollment_reader_id)
    ) {
      return readers;
    }
    return [
      {
        id: nextPolicy.enrollment_reader_id,
        name: nextPolicy.enrollment_reader_name ?? nextPolicy.enrollment_reader_id,
        kind: 'reader',
        status: 'not currently listed',
      },
      ...readers,
    ];
  }, [nextPolicy, readers]);

  if (state.status !== 'authed' || !isMaster) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!nextPolicy) return;
    if (enablingRiskyFlow && enableAck.trim() !== 'ENABLE UNVERIFIED CREDENTIALS') {
      setError(
        'Type ENABLE UNVERIFIED CREDENTIALS before enabling touch credentials or third-party NFC.',
      );
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await apiClient.post<{ data: CredentialPolicy }>(
        '/api/v1/admin/credentials/policy',
        {
          ...nextPolicy,
          reason,
        },
        { skipTenantScope: true },
      );
      setPolicy(res.data);
      setNextPolicy(res.data);
      setReason('');
      setEnableAck('');
      setNotice('Credential policy updated.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save policy.');
    } finally {
      setSaving(false);
    }
  }

  async function onCallRoutingSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSavingCallRouting(true);
    setError(null);
    setNotice(null);
    try {
      const payload: {
        enabled: boolean;
        username: string | null;
        password?: string;
        totp_secret?: string;
        profile_dir: string | null;
        console_url: string | null;
      } = {
        enabled: callRoutingForm.enabled,
        username: callRoutingForm.username.trim() || null,
        profile_dir: callRoutingForm.profile_dir.trim() || null,
        console_url: callRoutingForm.console_url.trim() || null,
      };
      if (callRoutingForm.password.length > 0) {
        payload.password = callRoutingForm.password;
      }
      if (callRoutingForm.totp_secret.trim().length > 0) {
        payload.totp_secret = callRoutingForm.totp_secret.trim();
      }
      const res = await apiClient.put<{ data: CallRoutingAutomationStatus }>(
        '/api/v1/admin/callboxes/call-routing-automation',
        payload,
        { skipTenantScope: true },
      );
      setCallRouting(res.data);
      setCallRoutingForm((prev) => ({
        ...prev,
        enabled: res.data.enabled,
        username: res.data.config.username ?? '',
        password: '',
        totp_secret: '',
        profile_dir: res.data.config.profile_dir ?? prev.profile_dir,
      }));
      setNotice('Call routing automation settings updated.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save call routing settings.');
    } finally {
      setSavingCallRouting(false);
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="flex items-center gap-4">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
          <Icon name="key" className="text-2xl" />
        </div>
        <div>
          <h1 className="font-headline-md text-headline-md text-on-surface">Credential policy</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">
            Master admin controls for touch credentials and third-party NFC.
          </p>
        </div>
      </header>

      {notice ? (
        <div
          role="status"
          className="bg-secondary-container text-on-secondary-container rounded-lg px-4 py-2 font-body-md text-body-md"
        >
          {notice}
        </div>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="bg-error-container text-on-error-container rounded-lg px-4 py-2 font-body-md text-body-md"
        >
          {error}
        </div>
      ) : null}

      <div className="rounded-lg border border-outline-variant bg-surface-container px-4 py-3 text-on-surface">
        <p className="font-label-md text-label-md">Safety warning</p>
        <p className="font-body-md text-body-md text-on-surface-variant mt-1">
          Touch credentials and third-party NFC are disabled by default. NFC assignment and
          unassignment are not live-verified on every controller yet. Enable these only if your site
          has accepted that risk and has a rollback plan.
        </p>
      </div>

      {!policy || !nextPolicy ? (
        <div className="p-12 flex justify-center">
          <Spinner />
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="space-y-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
        >
          <PolicyRow
            title="Enable touch credentials"
            description="Allows master admins to assign touch credentials to employees."
            checked={nextPolicy.touch_credential_enabled}
            onChange={(checked) =>
              setNextPolicy((prev) =>
                prev ? { ...prev, touch_credential_enabled: checked } : prev,
              )
            }
          />

          <PolicyRow
            title="Enable third-party NFC"
            description="Allows NFC token issue and NFC reader enrollment flows."
            checked={nextPolicy.third_party_nfc_enabled}
            onChange={(checked) =>
              setNextPolicy((prev) => (prev ? { ...prev, third_party_nfc_enabled: checked } : prev))
            }
          />

          <PolicyRow
            title="Allow PIN codes"
            description="When disabled, no new PIN codes can be issued. Employees with existing PINs keep them until manually removed."
            checked={nextPolicy.pin_code_enabled}
            onChange={(checked) =>
              setNextPolicy((prev) => (prev ? { ...prev, pin_code_enabled: checked } : prev))
            }
          />

          {policy.pin_code_enabled && !nextPolicy.pin_code_enabled && (
            <div className="rounded-lg border border-tertiary-fixed-dim bg-tertiary-fixed p-3 text-on-tertiary-fixed">
              <p className="font-semibold text-sm">Disabling PIN codes</p>
              <p className="mt-1 text-sm text-on-tertiary-fixed-variant">
                Employees with existing PINs will keep them until a manager manually removes them.
                New PINs cannot be issued once this is saved.
              </p>
            </div>
          )}

          <div>
            <label
              htmlFor="touch-pass-management-url"
              className="font-label-md text-label-md text-on-surface block mb-1"
            >
              UniFi Touch Pass management URL
            </label>
            <input
              id="touch-pass-management-url"
              type="url"
              value={nextPolicy.touch_pass_management_url ?? ''}
              onChange={(e) =>
                setNextPolicy((prev) =>
                  prev
                    ? {
                        ...prev,
                        touch_pass_management_url: e.target.value.trim() || null,
                      }
                    : prev,
                )
              }
              maxLength={2048}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              placeholder="https://unifi.ui.com/consoles/.../access/settings/touch-pass"
            />
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              Optional shortcut shown on Credential inventory for super admins.
            </p>
          </div>

          <div>
            <label
              htmlFor="enrollment-reader"
              className="font-label-md text-label-md text-on-surface block mb-1"
            >
              Enrollment reader
            </label>
            <select
              id="enrollment-reader"
              value={
                nextPolicy.enrollment_reader_mode === 'specified'
                  ? (nextPolicy.enrollment_reader_id ?? '')
                  : ''
              }
              onChange={(e) => {
                const reader = readerOptions.find((row) => row.id === e.target.value);
                setNextPolicy((prev) =>
                  prev
                    ? {
                        ...prev,
                        enrollment_reader_mode: reader ? 'specified' : 'any',
                        enrollment_reader_id: reader?.id ?? null,
                        enrollment_reader_name: reader?.name ?? null,
                      }
                    : prev,
                );
              }}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
            >
              <option value="">Choose per enrollment</option>
              {readerOptions.map((reader) => (
                <option key={reader.id} value={reader.id}>
                  {readerOptionLabel(reader)}
                </option>
              ))}
            </select>
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              When set, all tenant enrollment flows use this reader. When unset, the enrollment page
              shows the available reader list for the operator to choose from.
            </p>
            {readerLoadError ? (
              <p className="mt-1 font-body-sm text-body-sm text-error" role="alert">
                {readerLoadError}
              </p>
            ) : readers.length === 0 ? (
              <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
                No reader or intercom devices are in the building resource pool yet. Refresh
                hardware resources after the controller is connected.
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="policy-reason"
              className="font-label-md text-label-md text-on-surface block mb-1"
            >
              Reason for change
            </label>
            <textarea
              id="policy-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              placeholder="Explain why you are changing this policy."
            />
          </div>

          {enablingRiskyFlow ? (
            <div className="rounded-lg border border-error bg-error-container/40 p-3 text-on-surface">
              <p className="font-label-md text-label-md text-on-surface">
                Extra confirmation required
              </p>
              <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
                You are enabling credential paths that are not fully live-verified on every
                controller and firmware combination. To proceed, type
                <code className="mx-1 rounded bg-surface px-1 py-0.5">
                  ENABLE UNVERIFIED CREDENTIALS
                </code>
                exactly.
              </p>
              <label
                htmlFor="enable-risk-ack"
                className="mt-3 block font-label-md text-label-md text-on-surface"
              >
                Confirmation phrase
              </label>
              <input
                id="enable-risk-ack"
                value={enableAck}
                onChange={(e) => setEnableAck(e.target.value)}
                className="mt-1 w-full rounded border border-outline-variant bg-surface px-3 py-2 text-on-surface"
                placeholder="ENABLE UNVERIFIED CREDENTIALS"
                autoComplete="off"
              />
            </div>
          ) : null}

          <div className="rounded-lg bg-surface-container p-3 font-body-md text-body-md text-on-surface-variant">
            <p>Current policy:</p>
            <ul className="list-disc ml-6">
              <li>Touch credentials: {policyStateLabel(policy.touch_credential_enabled)}</li>
              <li>Third-party NFC: {policyStateLabel(policy.third_party_nfc_enabled)}</li>
              <li>PIN codes: {policyStateLabel(policy.pin_code_enabled)}</li>
              <li>
                UniFi Touch Pass link:{' '}
                {policy.touch_pass_management_url ? 'configured' : 'not configured'}
              </li>
              <li>
                Enrollment reader:{' '}
                {policy.enrollment_reader_mode === 'specified' && policy.enrollment_reader_id
                  ? (policy.enrollment_reader_name ?? policy.enrollment_reader_id)
                  : 'choose per enrollment'}
              </li>
            </ul>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setNextPolicy(policy);
                setReason('');
                setEnableAck('');
                setError(null);
              }}
              disabled={saving}
            >
              Reset
            </Button>
            <Button type="submit" variant="primary" loading={saving}>
              Save policy
            </Button>
          </div>
        </form>
      )}

      <form
        onSubmit={onCallRoutingSubmit}
        className="space-y-4 bg-surface-container-lowest border border-outline-variant rounded-xl p-6"
      >
        <div>
          <h2 className="font-title-lg text-title-lg text-on-surface">Call routing automation</h2>
          <p className="mt-1 font-body-md text-body-md text-on-surface-variant">
            Technician-only settings for syncing reader doorbell phone targets to UniFi Access.
          </p>
        </div>

        <PolicyRow
          title="Enable call routing automation"
          description="Allows the backend to update callbox button routing after the automation account and Access console URL are configured."
          checked={callRoutingForm.enabled}
          onChange={(checked) => setCallRoutingForm((prev) => ({ ...prev, enabled: checked }))}
        />

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label
              htmlFor="call-routing-username"
              className="font-label-md text-label-md text-on-surface block mb-1"
            >
              UniFi automation account
            </label>
            <input
              id="call-routing-username"
              value={callRoutingForm.username}
              onChange={(e) =>
                setCallRoutingForm((prev) => ({ ...prev, username: e.target.value }))
              }
              maxLength={320}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              autoComplete="username"
            />
          </div>

          <div>
            <label
              htmlFor="call-routing-password"
              className="font-label-md text-label-md text-on-surface block mb-1"
            >
              Password
            </label>
            <input
              id="call-routing-password"
              type="password"
              value={callRoutingForm.password}
              onChange={(e) =>
                setCallRoutingForm((prev) => ({ ...prev, password: e.target.value }))
              }
              maxLength={1024}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              placeholder={
                callRouting?.config.password_configured
                  ? 'Password is set; enter a new one to replace it'
                  : 'Set password'
              }
              autoComplete="new-password"
            />
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              {callRouting?.config.password_configured
                ? 'Password configured.'
                : 'No password set.'}
            </p>
          </div>

          <div>
            <label
              htmlFor="call-routing-totp-secret"
              className="font-label-md text-label-md text-on-surface block mb-1"
            >
              Authenticator setup key
            </label>
            <input
              id="call-routing-totp-secret"
              type="password"
              value={callRoutingForm.totp_secret}
              onChange={(e) =>
                setCallRoutingForm((prev) => ({ ...prev, totp_secret: e.target.value }))
              }
              maxLength={256}
              className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
              placeholder={
                callRouting?.config.totp_secret_configured
                  ? 'TOTP seed is set; enter a new one to replace it'
                  : 'Set TOTP seed'
              }
              autoComplete="off"
            />
            <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
              {callRouting?.config.totp_secret_configured
                ? 'Authenticator key configured.'
                : 'No authenticator key set.'}
            </p>
          </div>
        </div>

        <div>
          <label
            htmlFor="call-routing-console-url"
            className="font-label-md text-label-md text-on-surface block mb-1"
          >
            UniFi Access page URL
          </label>
          <input
            id="call-routing-console-url"
            type="url"
            value={callRoutingForm.console_url}
            onChange={(e) =>
              setCallRoutingForm((prev) => ({ ...prev, console_url: e.target.value }))
            }
            maxLength={2048}
            className="w-full px-3 py-2 bg-surface border border-outline-variant rounded text-on-surface"
            placeholder="https://unifi.ui.com/consoles/.../access"
          />
          <p className="mt-1 font-body-sm text-body-sm text-on-surface-variant">
            Open UniFi in a browser, switch to the correct console, open the Access app, then copy
            the browser address. It should look like
            {' '}
            <span className="font-code text-code">
              https://unifi.ui.com/consoles/.../access
            </span>
            .
          </p>
        </div>

        {callRouting ? (
          <div className="rounded-lg bg-surface-container p-3 font-body-md text-body-md text-on-surface-variant">
            <p>Status: {callRouting.mode.replace(/_/g, ' ')}</p>
            <ul className="list-disc ml-6 mt-1">
              <li>Automation account: {callRouting.config.username ?? 'not set'}</li>
              <li>Password: {callRouting.config.password_configured ? 'configured' : 'not set'}</li>
              <li>
                Authenticator key:{' '}
                {callRouting.config.totp_secret_configured ? 'configured' : 'not set'}
              </li>
              <li>
                Browser session:{' '}
                {callRouting.config.profile_dir
                  ? callRouting.config.profile_dir_exists
                    ? 'ready'
                    : 'needs technician setup'
                  : 'not set'}
              </li>
              <li>
                UniFi Access page URL:{' '}
                {callRouting.config.console_url_configured ? 'configured' : 'not configured'}
              </li>
            </ul>
            {callRouting.requirements.length > 0 ? (
              <ul className="list-disc ml-6 mt-2">
                {callRouting.requirements.map((requirement) => (
                  <li key={requirement}>{requirement}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button type="submit" variant="primary" loading={savingCallRouting}>
            Save automation settings
          </Button>
        </div>
      </form>
    </div>
  );
}

function PolicyRow({
  title,
  description,
  checked,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 p-3 border border-outline-variant rounded-lg">
      {/* The wrapping <label> is the activation target, so WCAG 2.5.8 is met
          by the row. The native 13px box is still a small affordance for the
          site-wide credential switches — including the PIN opt-in — so it is
          sized to the text beside it. */}
      <input
        type="checkbox"
        className="mt-0.5 h-5 w-5 shrink-0 accent-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <div>
        <div className="font-label-md text-label-md text-on-surface">{title}</div>
        <div className="font-body-md text-body-md text-on-surface-variant">{description}</div>
      </div>
    </label>
  );
}
